import React from 'react';
import {Box, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper} from '@mui/material';

/** Column bone kinds aligned with BS/PL thead (fs + adjustment). */
export type FsSkeletonCol =
    | {kind: 'name'; width?: number | string}
    | {kind: 'chip'; width?: number | string}
    | {kind: 'money'; width?: number | string}
    | {kind: 'text'; width?: number | string};

const DEFAULT_ROW_COUNT = 12;

/** Vary name bone width / indent so hierarchy reads like real FS rows. */
const NAME_ROW_PATTERN: Array<{indent: number; widthPct: number}> = [
    {indent: 0, widthPct: 72},
    {indent: 2, widthPct: 58},
    {indent: 2, widthPct: 64},
    {indent: 4, widthPct: 48},
    {indent: 4, widthPct: 52},
    {indent: 2, widthPct: 60},
    {indent: 0, widthPct: 68},
    {indent: 2, widthPct: 55},
    {indent: 4, widthPct: 44},
    {indent: 4, widthPct: 50},
    {indent: 2, widthPct: 62},
    {indent: 0, widthPct: 70},
];

function cellBone(col: FsSkeletonCol, rowIndex: number): React.ReactNode {
    if (col.kind === 'name') {
        const pat = NAME_ROW_PATTERN[rowIndex % NAME_ROW_PATTERN.length];
        return (
            <Skeleton
                variant="rounded"
                animation="wave"
                height={22}
                width={`${pat.widthPct}%`}
                sx={{ml: pat.indent, maxWidth: '100%'}}
            />
        );
    }
    if (col.kind === 'chip') {
        return <Skeleton variant="rounded" animation="wave" width={36} height={22} sx={{mx: 'auto'}}/>;
    }
    if (col.kind === 'money') {
        return <Skeleton variant="rounded" animation="wave" width="78%" height={28} sx={{mx: 'auto'}}/>;
    }
    return <Skeleton variant="text" animation="wave" width="70%" height={20} sx={{mx: 'auto'}}/>;
}

export interface FsStatementTableSkeletonRowsProps {
    columns: FsSkeletonCol[];
    rowCount?: number;
}

/** TableBody rows only � drop into existing BS/PL tables under real thead. */
export function FsStatementTableSkeletonRows({
    columns,
    rowCount = DEFAULT_ROW_COUNT,
}: FsStatementTableSkeletonRowsProps) {
    return (
        <>
            {Array.from({length: rowCount}, (_, rowIndex) => (
                <TableRow key={`fs-skel-${rowIndex}`}>
                    {columns.map((col, colIndex) => (
                        <TableCell
                            key={`fs-skel-${rowIndex}-${colIndex}`}
                            align={col.kind === 'name' ? 'left' : 'center'}
                            sx={{
                                width: col.width,
                                minWidth: typeof col.width === 'number' ? col.width : undefined,
                                py: 0.75,
                                px: col.kind === 'name' ? 1 : 0.75,
                            }}
                        >
                            {cellBone(col, rowIndex)}
                        </TableCell>
                    ))}
                </TableRow>
            ))}
        </>
    );
}

/** FS Balance Sheet amount columns (Current / Last Year / Change / Remark). */
export function buildFsBsSkeletonColumns(opts: {
    hideLastYearColumns?: boolean;
    lastYearOnly?: boolean;
    showRemark?: boolean;
} = {}): FsSkeletonCol[] {
    const {hideLastYearColumns, lastYearOnly, showRemark} = opts;
    const cols: FsSkeletonCol[] = [
        {kind: 'name', width: showRemark ? '28%' : '36.5%'},
    ];
    if (showRemark) cols.push({kind: 'text', width: '32%'});
    if (!lastYearOnly) cols.push({kind: 'money', width: 188});
    if (!hideLastYearColumns) {
        cols.push({kind: 'money', width: 188});
        if (!lastYearOnly) cols.push({kind: 'text', width: 120});
    }
    return cols;
}

/** FS Profit & Loss (SINGLE Current or MULTI YOA�N + LY + Change). */
export function buildFsPlSkeletonColumns(opts: {
    hideLastYearColumns?: boolean;
    lastYearOnly?: boolean;
    showRemark?: boolean;
    /** YOA headers length; ?2 ? MULTI amount cols */
    yoaCount?: number;
} = {}): FsSkeletonCol[] {
    const {hideLastYearColumns, lastYearOnly, showRemark, yoaCount = 0} = opts;
    const isMulti = yoaCount >= 2;
    const cols: FsSkeletonCol[] = [
        {kind: 'name', width: showRemark ? '28%' : '36.5%'},
    ];
    if (showRemark) cols.push({kind: 'text', width: '32%'});
    if (!lastYearOnly) {
        if (isMulti) {
            for (let i = 0; i < yoaCount; i++) cols.push({kind: 'money', width: 140});
        } else {
            cols.push({kind: 'money', width: 188});
        }
    }
    if (!hideLastYearColumns) {
        cols.push({kind: 'money', width: isMulti ? 160 : 188});
        if (!lastYearOnly) cols.push({kind: 'text', width: 120});
    }
    return cols;
}

/**
 * Adjustment BS/PL: name + chip + Client(+YOA) + optional Opening Adj + adjust cols + Audited + LY/Change(+Ratio).
 */
export function buildAdjustmentSkeletonColumns(opts: {
    statement: 'bs' | 'pl';
    adjustColumnCount?: number;
    hideLastYearColumns?: boolean;
    fsOpeningEditMode?: boolean;
    /** Expanded client-by-YOA columns (PL); default 1 = single Client */
    clientColumnCount?: number;
}): FsSkeletonCol[] {
    const {
        statement,
        adjustColumnCount = 0,
        hideLastYearColumns,
        fsOpeningEditMode,
        clientColumnCount = 1,
    } = opts;
    const cols: FsSkeletonCol[] = [
        {kind: 'name', width: 400},
        {kind: 'chip', width: 50},
    ];
    const clientN = Math.max(1, clientColumnCount);
    for (let i = 0; i < clientN; i++) cols.push({kind: 'money', width: 160});
    if (fsOpeningEditMode) cols.push({kind: 'money', width: 160});
    for (let i = 0; i < Math.max(0, adjustColumnCount); i++) {
        cols.push({kind: 'money', width: 120});
    }
    cols.push({kind: 'money', width: 160}); // Audited
    if (!hideLastYearColumns) {
        cols.push({kind: 'money', width: 160});
        cols.push({kind: 'text', width: 120});
        if (statement === 'pl') cols.push({kind: 'text', width: 88}); // Ratio
    }
    return cols;
}

export interface FsDetailShellSkeletonProps {
    height?: number | string;
    flex?: number | string;
    minHeight?: number | string;
}

/**
 * FsDetail outer shell: left BS/PL tabs + right report header + column-aware table bones.
 */
export function FsDetailShellSkeleton({
    height,
    flex,
    minHeight,
}: FsDetailShellSkeletonProps) {
    const columns = buildFsBsSkeletonColumns();
    return (
        <Box sx={{
            height,
            flex,
            minHeight,
            display: 'flex',
            gap: 1,
            width: '100%',
            overflow: 'hidden',
        }}>
            <Box sx={{
                width: 200,
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
                pr: 1,
                pt: 0.5,
            }}>
                <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 1}}>
                    <Skeleton variant="circular" width={28} height={28}/>
                </Box>
                <Stack spacing={0.75}>
                    {[0, 1].map((i) => (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1,
                                py: 1,
                                borderRadius: 1,
                                bgcolor: i === 0 ? 'action.selected' : 'transparent',
                            }}
                        >
                            <Skeleton variant="rounded" width={24} height={24}/>
                            <Skeleton variant="text" width={i === 0 ? 110 : 130} height={22}/>
                        </Box>
                    ))}
                </Stack>
            </Box>
            <Box sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                p: 1,
            }}>
                <Stack spacing={0.5} mb={1}>
                    <Skeleton variant="text" width="42%" height={28}/>
                    <Skeleton variant="text" width="28%" height={22}/>
                    <Skeleton variant="text" width="36%" height={20}/>
                </Stack>
                <TableContainer component={Paper} sx={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
                    <Table size="small" stickyHeader sx={{tableLayout: 'fixed', width: '100%'}}>
                        <TableHead>
                            <TableRow>
                                {columns.map((col, i) => (
                                    <TableCell
                                        key={`shell-h-${i}`}
                                        align={col.kind === 'name' ? 'left' : 'center'}
                                        sx={{width: col.width, py: 1}}
                                    >
                                        {col.kind === 'name' ? null : (
                                            <Skeleton variant="text" width="55%" height={18} sx={{mx: 'auto'}}/>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <FsStatementTableSkeletonRows columns={columns} rowCount={10}/>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
}
